import fp from 'lodash/fp';
import React from 'react';
import { Button, Col, Grid, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { LinkContainer } from 'react-router-bootstrap';
import Redirect from 'react-router/Redirect';
import { branch, compose, renderComponent, withProps } from 'recompose';
import { routes, accountantsIndustryIdentifier} from '../../../constants';
import { getPlanFreeTrialDaysByPlanId } from '../../../services';
import { noCapMap } from '../../../utils';
import { Icon } from '../../components';
import Loading from '../loading';
import { getIndustryFromIdentifier, getNext, getOnboardingProcessFromPath } from './industry';

const Plan = ({
  details,
  link,
  pricing,
  title,
}) => (
    <Col xs={12} md={6} className='plan'>
      <div className='title'>
        {title}
      </div>
      {pricing}
      <ul>
        {
          noCapMap((detail, index) =>
            <li key={index}>
              {detail}
            </li>
          )(details)
        }
      </ul>
      {link}
    </Col>
  );

const Plans = branch(
  ({ plans }) => plans.length === 0,
  renderComponent(Loading)
)(({
  individualPlan,
  smallTeamPlan,
  individualPlanFreeTrialDays,
  smallTeamPlanFreeTrialDays,
  nextUrl
}) => (
    <Row className='xs-gutter'>
      <Plan {...{
        title: <h2>Individual user</h2>,
        pricing: (
          <div className='pricing'>
            <span>${individualPlan.unitPrice.amount}</span> /{individualPlan.interval.interval} (ex GST)
          </div>
        ),
        details: [
          'Access to curated and relevant content from publishers you know and love',
          'Tailored CPD plan based on your CPD categories',
          'Intuitive dashboard with real-time tracking',
          'Events, Seminars, Webinars, never miss out again!',
          'Notifications to keep you on track with your CPD and compliance'
        ],
        link: (
          <LinkContainer to={nextUrl(individualPlan.id)}>
            <Button bsStyle='green'>
              {individualPlanFreeTrialDays} day free trial <Icon icon='chevron-right-medium' />
            </Button>
          </LinkContainer>
        )
      }} />
      <Plan {...{
        title: <h2>Organisations</h2>,
        pricing: (
          <div className='pricing'>
            <span>${smallTeamPlan.unitPrice.amount}</span> /{smallTeamPlan.interval.interval} per user (ex GST)
        </div>
        ),
        details: [
          <strong>Everything an individual users gets plus:</strong>,
          'Overview of your teams progress so you can spot risks before they happen',
          'Real time reporting on adviser CPD and compliance',
          'Ability to track multi-team and individual CPD and compliance progress',
          'Dedicated Caddie Crew member to help onboard your organisation'
        ],
        link: (
          <LinkContainer to={nextUrl(smallTeamPlan.id)}>
            <Button bsStyle='green'>
              {smallTeamPlanFreeTrialDays} day free trial <Icon icon='chevron-right-medium' />
            </Button>
          </LinkContainer>
        )
      }} />
    </Row>
  ));

/**
 * Provides the view of Caddie subscription plan selection page.
 */
const ChoosePlan = ({
  individualPlan,
  plans,
  smallTeamPlan,
  individualPlanFreeTrialDays,
  smallTeamPlanFreeTrialDays,
  nextUrl
}) => (
    <section className='choose-plan'>
      <Grid>
        <header>
          <Row>
            <Col xs={12} md={7} mdOffset={1}>
              <h1>
                Flexible plans to help get your education and compliance on par with your professional obligations
              </h1>
            </Col>
          </Row>
        </header>
        <main>
          <Row>
            <Col xs={12} md={10} mdOffset={1}>
              <Plans {...{
                individualPlan,
                plans,
                smallTeamPlan,
                individualPlanFreeTrialDays,
                smallTeamPlanFreeTrialDays,
                nextUrl
              }} />
            </Col>
          </Row>
        </main>
      </Grid>
    </section>
  );

const mapStateToProps = (state) => ({
  plans: state.payment.plans,
  config: state.configuration,
  industriesWithCpdCategorys: state.industry.industriesWithCpdCategorys
});

/**
 * Get the stripe price id for individual user,
 * Accountants and Bookkeepers industry will have a different price plan. 
 */
const getIndividualPlanId = ({
  Individual,
  BASIndividual,
}, signupIndustry) => {
  if (signupIndustry === accountantsIndustryIdentifier) {
    return BASIndividual;
  } else {
    return Individual;
  }
}

/**
 * Get the stripe price id for business,
 * Accountants and Bookkeepers industry will have a different price plan.
 */
const getOrganizationPlanId = ({
  SmallTeam,
  BASSmallTeam,
}, signupIndustry) => {
  if (signupIndustry === accountantsIndustryIdentifier) {
    return BASSmallTeam;
  } else {
    return SmallTeam;
  }
}

const findPlan = (planId) => fp.find(({ id }) => id === planId);

export default compose(
  connect(mapStateToProps),
  branch(
    ({ config }) => !config.stripeConfig,
    renderComponent(Loading)
  ),
  withProps(({
    match: {
      path,
      params: {
        industryIdentifier
      }
    },
    config: {
      stripeConfig: {
        plansInUse
      }
    },
    plans,
    industriesWithCpdCategorys
  }) => ({
    individualPlan: findPlan(getIndividualPlanId(plansInUse, industryIdentifier))(plans),
    smallTeamPlan: findPlan(getOrganizationPlanId(plansInUse, industryIdentifier))(plans),
    signupIndustry: getIndustryFromIdentifier(industryIdentifier, industriesWithCpdCategorys)
  })),
  branch(
    ({ signupIndustry, individualPlan, smallTeamPlan }) => {
      return signupIndustry === undefined || individualPlan === undefined || smallTeamPlan === undefined
    },
    renderComponent(() => <Redirect to={routes.onboarding.invalid} />)
  ),
  withProps(({
    config: {
      stripeConfig: {
        plansInUse
      }
    },
    match: {
      path,
    },
    individualPlan,
    smallTeamPlan,
    signupIndustry
  }) => ({
    individualPlanFreeTrialDays: getPlanFreeTrialDaysByPlanId(plansInUse, individualPlan.id),
    smallTeamPlanFreeTrialDays: getPlanFreeTrialDaysByPlanId(plansInUse, smallTeamPlan.id),
    nextUrl: getNext(signupIndustry, getOnboardingProcessFromPath(path))
  }))
)(ChoosePlan);



// WEBPACK FOOTER //
// ./src/containers/pages/onboarding/choose-plan.js